import React, { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { inputCls, Field } from "./_shared";

const EMPTY_NEW_SPONSOR = { company_name: "", website: "", logo_path: "", is_active: true, sort_order: 0 };

export default function SponsorsAdmin() {
  const [requests, setRequests] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_NEW_SPONSOR);
  const [creatingSaving, setCreatingSaving] = useState(false);
  const [creatingUploading, setCreatingUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/sponsor-applications"),
        api.get("/admin/sponsors"),
      ]);
      setRequests(r.data);
      setSponsors(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setReqStatus = async (id, status) => {
    try {
      await api.patch(`/admin/sponsor-applications/${id}`, { status });
      toast.success(`Marked ${status}`);
      const req = requests.find((x) => x.id === id);
      if (status === "accepted" && req) {
        await api.post("/admin/sponsors", {
          company_name: req.company_name,
          website: req.website,
          logo_path: "",
          is_active: false,
          sort_order: sponsors.length,
          application_id: req.id,
        });
        toast.success("Sponsor draft created — upload logo to publish");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Delete this sponsor request?")) return;
    await api.delete(`/admin/sponsor-applications/${id}`);
    load();
  };

  const uploadLogo = async (sponsorId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/admin/sponsors/${sponsorId}`, {
        company_name: sponsors.find((x) => x.id === sponsorId)?.company_name,
        website: sponsors.find((x) => x.id === sponsorId)?.website,
        logo_path: data.path,
        is_active: true,
        sort_order: sponsors.find((x) => x.id === sponsorId)?.sort_order || 0,
      });
      toast.success("Logo uploaded, sponsor published");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
  };

  const saveSponsor = async (s) => {
    await api.patch(`/admin/sponsors/${s.id}`, {
      company_name: s.company_name,
      website: s.website,
      logo_path: s.logo_path || "",
      is_active: s.is_active,
      sort_order: parseInt(s.sort_order) || 0,
    });
    toast.success("Saved");
    load();
  };

  const deleteSponsor = async (id) => {
    if (!window.confirm("Delete this sponsor from the homepage?")) return;
    await api.delete(`/admin/sponsors/${id}`);
    load();
  };

  const openCreate = () => {
    setNewForm({ ...EMPTY_NEW_SPONSOR, sort_order: sponsors.length });
    setCreating(true);
  };

  const uploadNewLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCreatingUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewForm((f) => ({ ...f, logo_path: data.path }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setCreatingUploading(false);
    }
  };

  const submitNewSponsor = async (e) => {
    e.preventDefault();
    setCreatingSaving(true);
    try {
      await api.post("/admin/sponsors", {
        company_name: newForm.company_name.trim(),
        website: newForm.website.trim(),
        logo_path: newForm.logo_path || "",
        is_active: !!newForm.is_active,
        sort_order: parseInt(newForm.sort_order) || 0,
      });
      toast.success("Sponsor added");
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setCreatingSaving(false);
    }
  };

  const badgeCls = (status) => ({
    pending: "bg-white/10 text-white",
    accepted: "bg-success text-black",
    rejected: "bg-danger text-white",
  }[status] || "bg-white/10 text-white");

  if (loading) return <div className="text-white/50 font-mono text-sm uppercase tracking-widest py-16 text-center">Loading…</div>;

  return (
    <div data-testid="sponsors-admin" className="space-y-14">
      <section>
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-2">Sponsor requests</div>
        <h1 className="font-heading text-4xl font-black uppercase tracking-tighter mb-6">{requests.length} received</h1>
        {requests.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center text-white/50" data-testid="sponsor-req-empty">No sponsor requests yet.</div>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} data-testid={`sponsor-req-${r.id}`} className="bg-surface border border-white/10 p-5 grid md:grid-cols-6 gap-4 items-start">
                <div className="md:col-span-2">
                  <div className="font-heading text-lg font-bold uppercase truncate">{r.company_name}</div>
                  <div className="text-xs font-mono text-white/50 mt-1">{r.contact_name}</div>
                  <div className="text-xs font-mono text-white/40 mt-1">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <div className="md:col-span-2 text-sm space-y-1">
                  <a href={`mailto:${r.email}`} className="block text-lime hover:underline break-all">{r.email}</a>
                  <a href={`tel:${r.phone}`} className="block text-white/80 hover:text-lime">{r.phone}</a>
                  <a href={r.website?.startsWith("http") ? r.website : `https://${r.website}`} target="_blank" rel="noreferrer" className="block text-white/60 hover:text-lime text-xs break-all">{r.website}</a>
                  <div className="text-xs font-mono text-white/40">{r.country} · {r.months_commitment} mo · VAT {r.vat_number || "—"}</div>
                  {r.message && <div className="text-white/60 text-xs mt-1 whitespace-pre-wrap">{r.message}</div>}
                </div>
                <div>
                  <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold ${badgeCls(r.status)}`}>{r.status}</span>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  {r.status !== "accepted" && (
                    <button data-testid={`sponsor-req-accept-${r.id}`} onClick={() => setReqStatus(r.id, "accepted")} className="px-3 py-1.5 border border-white/10 text-[10px] font-mono uppercase tracking-widest hover:border-success hover:text-success">Accept</button>
                  )}
                  {r.status !== "rejected" && (
                    <button data-testid={`sponsor-req-reject-${r.id}`} onClick={() => setReqStatus(r.id, "rejected")} className="px-3 py-1.5 border border-white/10 text-[10px] font-mono uppercase tracking-widest hover:border-danger hover:text-danger">Reject</button>
                  )}
                  <button data-testid={`sponsor-req-delete-${r.id}`} onClick={() => deleteRequest(r.id)} className="px-3 py-1.5 border border-white/10 text-[10px] font-mono uppercase tracking-widest hover:border-danger hover:text-danger">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-2">Active sponsors on homepage</div>
            <h1 className="font-heading text-4xl font-black uppercase tracking-tighter">{sponsors.filter((s) => s.is_active).length} live · {sponsors.length} total</h1>
          </div>
          <button
            data-testid="new-sponsor-btn"
            onClick={openCreate}
            className="px-4 py-2 bg-lime text-black text-xs font-mono uppercase tracking-widest font-bold hover:scale-[1.03] transition-transform"
          >
            + New sponsor
          </button>
        </div>
        {sponsors.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center text-white/50" data-testid="sponsor-empty">No sponsors yet. Click "+ New sponsor" to add one manually, or accept a request above.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sponsors-admin-grid">
            {sponsors.map((s) => (
              <SponsorCard key={s.id} sponsor={s} onSave={saveSponsor} onDelete={deleteSponsor} onUpload={uploadLogo} />
            ))}
          </div>
        )}
      </section>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="sponsor-form-modal" className="max-w-lg bg-surface border-white/10 rounded-none text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tighter">New sponsor</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitNewSponsor} className="space-y-4 mt-2">
            <Field label="Company / organization name *">
              <Input data-testid="new-sp-name" autoFocus required value={newForm.company_name} onChange={(e) => setNewForm((f) => ({ ...f, company_name: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Website / link target *">
              <Input data-testid="new-sp-web" required value={newForm.website} onChange={(e) => setNewForm((f) => ({ ...f, website: e.target.value }))} placeholder="https://…" className={inputCls} />
            </Field>
            <Field label="Logo">
              <div className="flex items-center gap-3">
                <input data-testid="new-sp-upload" type="file" accept="image/*" onChange={uploadNewLogo} className="text-xs text-white/60 file:bg-lime file:text-black file:font-mono file:uppercase file:text-[10px] file:tracking-widest file:border-0 file:px-3 file:py-1.5 file:mr-3" />
                {creatingUploading && <span className="text-xs font-mono text-white/50">Uploading…</span>}
                {newForm.logo_path && !creatingUploading && (
                  <img src={fileUrl(newForm.logo_path)} alt="logo" className="w-16 h-16 object-cover border border-white/10" />
                )}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/70">
                <input data-testid="new-sp-active" type="checkbox" checked={newForm.is_active} onChange={(e) => setNewForm((f) => ({ ...f, is_active: e.target.checked }))} />
                Live on homepage
              </label>
              <Field label="Order">
                <Input data-testid="new-sp-order" type="number" value={newForm.sort_order} onChange={(e) => setNewForm((f) => ({ ...f, sort_order: e.target.value }))} className={inputCls} />
              </Field>
            </div>
            <div className="flex justify-end pt-2">
              <button
                data-testid="new-sp-submit"
                type="submit"
                disabled={creatingSaving || !newForm.company_name.trim() || !newForm.website.trim()}
                className="px-8 py-3 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.03] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {creatingSaving ? "Saving…" : "Add sponsor →"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SponsorCard({ sponsor, onSave, onDelete, onUpload }) {
  const [s, setS] = useState(sponsor);
  useEffect(() => setS(sponsor), [sponsor]);
  const set = (k) => (e) => setS((prev) => ({ ...prev, [k]: e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  return (
    <div data-testid={`sponsor-card-${s.id}`} className="bg-surface border border-white/10 p-5 space-y-3">
      <div className="aspect-video bg-black border border-white/10 flex items-center justify-center p-4">
        {s.logo_path ? (
          <img src={fileUrl(s.logo_path)} alt={s.company_name} className="max-h-16 max-w-full object-contain" />
        ) : (
          <span className="text-white/30 font-mono text-xs uppercase tracking-widest">No logo yet</span>
        )}
      </div>
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-white/50">Company</Label>
        <Input data-testid={`sp-name-${s.id}`} value={s.company_name} onChange={set("company_name")} className={`mt-1 ${inputCls}`} />
      </div>
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-white/50">Website (link target)</Label>
        <Input data-testid={`sp-web-${s.id}`} value={s.website} onChange={set("website")} className={`mt-1 ${inputCls}`} />
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/70">
          <input data-testid={`sp-active-${s.id}`} type="checkbox" checked={!!s.is_active} onChange={set("is_active")} />
          Live
        </label>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-white/50">Order</Label>
          <Input data-testid={`sp-order-${s.id}`} type="number" value={s.sort_order || 0} onChange={set("sort_order")} className={`w-20 ${inputCls}`} />
        </div>
      </div>
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-white/50">Upload logo</Label>
        <input
          data-testid={`sp-upload-${s.id}`}
          type="file"
          accept="image/*"
          onChange={(e) => e.target.files?.[0] && onUpload(s.id, e.target.files[0])}
          className="mt-1 block text-xs text-white/70 file:bg-lime file:text-black file:font-mono file:uppercase file:text-[10px] file:tracking-widest file:border-0 file:px-3 file:py-1.5 file:mr-3"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button data-testid={`sp-save-${s.id}`} onClick={() => onSave(s)} className="flex-1 py-2 bg-lime text-black font-heading font-bold uppercase text-xs tracking-widest hover:scale-[1.02] transition-transform">Save</button>
        <button data-testid={`sp-delete-${s.id}`} onClick={() => onDelete(s.id)} className="px-3 py-2 border border-white/10 text-xs font-mono uppercase tracking-widest hover:border-danger hover:text-danger transition-colors">Delete</button>
      </div>
    </div>
  );
}
